/* eslint-disable max-lines */
import {
  BehaviorSubject,
  catchError, filter,
  firstValueFrom, map,
  Observable,
  of,
  retry, switchMap, take, throwError
} from 'rxjs';
// This file imports the `Axios` module.
import { AxiosError, AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import Axios from 'axios-observable';
import { Subject } from 'rxjs';
import { Isuccess } from '../types/return-types';
import { BizNestClientError } from './error-handlers';
import { FrontendLogger } from './frontend-logger';


interface IbizNestErrorResponse {
  err?: string;
  message?: string;
  stack?: string;
  code?: string;
}

export const getBizNestErrorResponseMessage = (error: AxiosError) => {
  const response: IbizNestErrorResponse = error.response?.data as IbizNestErrorResponse;

  if (response) {
    const errRes = response.err || response.message || 'Unknown error';

    return errRes;
  }

  if (error.message) {
    return error.message;
  }

  return 'Unknown error';
};

export const getBizNestErrorResponseCode = (error: AxiosError) => {
  const response: IbizNestErrorResponse = error.response?.data as IbizNestErrorResponse;

  if (response) {
    const errRes = response.code;

    return errRes;
  }

  return null;
};

// This interface defines the properties of a file.
export interface Ifile {
  // The filename of the file.
  filename?: string;
  // The revokable URL of the file.
  revokableUrl?: string;
  // The blob data of the file.
  blob: Blob;
  // The name of the field in the form that the file will be uploaded to.
  fieldname: string;
}

export interface IUploadProgress {
  percent: number;
  loaded: number;
  total: number;
}

export interface IUploadResponse<T> {
  progress: Observable<IUploadProgress>;
  response: Observable<T>;
}

interface IRetryAttempt {
  count: number;
  timestamp: number;
}

interface IRefreshRetryState {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '/auth/refresh': IRetryAttempt;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '/auth/ws-refresh': IRetryAttempt;
}


export interface IehttpControllerConfig extends AxiosRequestConfig {
  dontUserCookie?: boolean;
  authServiceBaseUrl?: string; // New: Base URL for the authentication microservice
  getRefreshTokenForRefresh?: () => Promise<string | null>;
  storeNewTokensAfterRefresh?: (accessToken: string, refreshToken: string) => Promise<void>;
  onRefreshFailure?: () => void; // New: Callback for when token refresh fails
}

// This class is a controller for handling HTTP requests.

/**
 * A class that provides methods for making HTTP requests using Axios.
 */
export class EhttpController {
  axiosInstance: Axios;
  private headers: { [key: string]: string } = {};
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private authServiceBaseUrl: string;
  private onRefreshFailure: () => void;
  private dontUserCookie: boolean;
  private getRefreshTokenForRefresh: () => Promise<string | null>;
  private storeNewTokensAfterRefresh: (accessToken: string, refreshToken: string) => Promise<void>;
  private currentAccessToken: string | null = null;
  private routesNotToRefresh: string[] = [
    '/auth/login',
    '/auth/signup',
    '/auth/password-resets/initiate',
    '/auth/password-resets/finalize',
    '/auth/verify-account',
    '/auth/resend-verification-code',
    '/auth/resend-verification-code/passwordless',
    '/auth/passwordless/signup',
    '/auth/passwordless/verify',
    '/auth/ws-login',
    '/auth/ws-signup',
    '/auth/logout',
    '/auth/ws-logout'
  ];

  private refreshRetryState: IRefreshRetryState = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '/auth/refresh': { count: 0, timestamp: 0 },
    // eslint-disable-next-line @typescript-eslint/naming-convention
    '/auth/ws-refresh': { count: 0, timestamp: 0 }
  };

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly REFRESH_RETRY_LIMIT = 5;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private readonly REFRESH_RETRY_WINDOW_MS = 60000; // 1 minute

  /**
   * The constructor for the class.
   * @param axiosInstance - An instance of Axios.
   */
  constructor(config: IehttpControllerConfig) {
    const mergedConfig: AxiosRequestConfig = {
      ...config,
      withCredentials: true // Crucial for sending and receiving HttpOnly cookies
    };

    this.axiosInstance = Axios.create(mergedConfig);

    this.dontUserCookie = config.dontUserCookie === true;

    // Set the authentication service base URL, defaulting to the current instance's baseURL if not provided
    this.authServiceBaseUrl = config.authServiceBaseUrl || config.baseURL || '';

    if (!this.authServiceBaseUrl) {
      FrontendLogger
        .warn('EhttpController: authServiceBaseUrl or baseURL not provided. Token refresh may not work correctly.');
    }
    // Set the refresh failure callback
    this.onRefreshFailure = config.onRefreshFailure || (() => {
      FrontendLogger
        .error('EhttpController: onRefreshFailure callback not provided. Defaulting to console log.');
    });

    // Initialize dontUserCookie-specific token management callbacks
    this.getRefreshTokenForRefresh = config.getRefreshTokenForRefresh || (() => {
      if (this.dontUserCookie) {
        FrontendLogger
          .warn(`EhttpController: getRefreshTokenForRefresh callback 
            not provided in dontUserCookie mode. Token refresh might fail.`);
      }

      return Promise.reject(new BizNestClientError('getRefreshTokenForRefresh callback not provided.'));
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    this.storeNewTokensAfterRefresh = config.storeNewTokensAfterRefresh || ((at, rt) => {
      if (this.dontUserCookie) {
        FrontendLogger.warn(`EhttpController: storeNewTokensAfterRefresh 
          callback not provided in dontUserCookie mode. New tokens will not be stored.`);
      }

      return Promise.reject(new BizNestClientError('storeNewTokensAfterRefresh callback not provided.'));
    });

    this.interceptorAxios();
  }


  /**
   * A static method that creates a new instance of the class.
   * @param baseURL - The base URL for the Axios instance.
   * @param token - The authorization token for the Axios instance.
   * @returns A new instance of the EhttpController class.
   */
  /* static create = <T = never>(config: AxiosRequestConfig<T>) => {
    FrontendLogger.debug('create:: - config:', config);
    // Create a new Axios instance.
    const instance = Axios.create(config);

    // Return a new instance of the class.
    return new EhttpController(instance);
  }; */

  getAxiosInstance() {
    return this.axiosInstance;
  }

  /**
   * A method that appends a token to the headers of the Axios instance.
   * @param token - The authorization token to append to the headers.
   */
  appendToken(token: string) {
    if (!token) {
      throw new BizNestClientError('Token is required');
    }

    if (typeof token !== 'string') {
      throw new BizNestClientError('Token must be of type string');
    }

    this.currentAccessToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

    if (!this.dontUserCookie) {
      FrontendLogger.warn(`appendToken:: - For web 
        (HttpOnly cookies), this method is generally not needed as the browser 
        handles Authorization automatically. Use it if you have specific 
        reasons to manually set the header.`);
    } else {
      FrontendLogger.debug('appendToken:: - currentAccessToken set for dontUserCookie use.');
    }
  }

  removeToken() {
    this.currentAccessToken = null;
    if (!this.dontUserCookie) {
      FrontendLogger.warn(`removeToken:: - For web (HttpOnly cookies), 
        clearing internal token does not log out the user. Consider calling your 
        backend logout endpoint instead.`);
    } else {
      // For dontUserCookie, this should be combined with clearing tokens from secure storage
      FrontendLogger.debug('removeToken:: - internal currentAccessToken cleared for dontUserCookie use.');
    }
  }

  interceptorAxios() {
    // Request Interceptor: Adds Authorization header for dontUserCookie, or other custom headers
    this.axiosInstance.interceptors.request.use(
      async(config) => {
        FrontendLogger.debug('Request Interceptor: config headers BEFORE merge:', config.headers);

        if (!config.headers) {
          config.headers = {};
        }

        // Apply any headers set via appendToHeaders/setHeaders
        if (this.headers && typeof this.headers === 'object') {
          Object.assign(config.headers, this.headers);
        }

        // Add Authorization header if in dontUserCookie mode AND currentAccessToken is available
        // For web, HttpOnly cookies are handled automatically by the browser
        if (this.dontUserCookie && this.currentAccessToken && typeof this.currentAccessToken === 'string') {
          config.headers['Authorization'] = this.currentAccessToken;
          FrontendLogger.debug('Request Interceptor: Added Authorization header for dontUserCookie.');
        } else if (!this.dontUserCookie) {
          FrontendLogger.debug('Request Interceptor: Relying on browser for HttpOnly cookie in web mode.');
        }

        FrontendLogger.debug('Request Interceptor: config headers AFTER merge:', config.headers);

        if (this.shouldAddRefreshToken(config) && config.method?.toLowerCase() === 'post' && !this.dontUserCookie) {
          try {
            const refreshToken = await this.getRefreshTokenForRefresh();

            if (refreshToken) {
              FrontendLogger.warn('Adding refresh token to request payload', refreshToken);
              config.data = {
                ...(config.data || {}),
                refresh_token: refreshToken,
                refreshToken // Include both formats for compatibility
              };
              FrontendLogger.warn('Added refresh token to request payload', config.data);
            }
          } catch (error) {
            FrontendLogger.warn('Could not add refresh token to request', error);
          }
        }

        return config;
      },
      (error) => {
        FrontendLogger.error(error, 'Request Interceptor error:');

        return Promise.reject(error);
      }
    );


    // Response Interceptor: Handles token refresh on 401 errors and auto-stores tokens
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check if we should store tokens from response body
        if (this.dontUserCookie && response.data) {
          const { access_token, refresh_token } = response.data;

          if (access_token && refresh_token) {
            this.storeNewTokensAfterRefresh(access_token, refresh_token)
              .catch(error => {
                FrontendLogger.error(error, 'Failed to store tokens from response:');
              });
          }
        }

        return response; // Pass through successful responses
      },
      async(error: AxiosError) => {
        const originalRequest = error.config;

        // Check retry limit for refresh endpoints
        if (originalRequest?.url &&
          this.isRefreshRetryLimitExceeded(originalRequest.url)) {
          this.onRefreshFailure();

          return Promise.reject(new BizNestClientError('Refresh retry limit exceeded'));
        }

        // If the error is 401 Unauthorized AND it's not the refresh token request itself
        // AND originalRequest exists (for retrying)

        if (error.response?.status === 401 && originalRequest &&
          !this.routesNotToRefresh.includes(originalRequest.url || '') &&
          // eslint-disable-next-line @typescript-eslint/naming-convention
          !(originalRequest as InternalAxiosRequestConfig & { _retry: boolean })._retry) {
          (originalRequest as InternalAxiosRequestConfig &
            // eslint-disable-next-line @typescript-eslint/naming-convention
            { _retry: boolean })._retry = true; // Mark this request to prevent infinite loops

          FrontendLogger.warn('401 Unauthorized received. Attempting token refresh...');

          if (!this.isRefreshing) {
            this.isRefreshing = true;
            this.refreshTokenSubject.next(false); // Clear previous token, signal no new token yet

            try {
              let refreshPayload: Record<string, unknown> = {};

              // For dontUserCookie mode, get refresh token from callback and send in body
              if (this.dontUserCookie) {
                const refreshToken = await this.getRefreshTokenForRefresh();

                if (!refreshToken) {
                  FrontendLogger
                    .error('refresh failed: No refresh token found from getRefreshTokenForRefresh callback.');
                  this.onRefreshFailure();

                  return await Promise
                    .reject(new BizNestClientError('Refresh token not found for dontUserCookie mode.'));
                }
                refreshPayload = {
                  refreshToken,
                  refresh_token: refreshToken
                }; // Backend expects 'refreshToken' in body for dontUserCookie mode
              }

              // Call your backend's refresh token endpoint using the dedicated authServiceBaseUrl
              FrontendLogger
                .debug(`Calling refresh token endpoint at: ${this.authServiceBaseUrl}/auth/refresh`);
              const refreshResponse = await firstValueFrom(Axios.post(
                `${this.authServiceBaseUrl}auth/refresh`,
                refreshPayload, // Send refresh token in body for dontUserCookie mode, or empty for web (cookies)
                { withCredentials: true } // Always true for consistency, relies on browser for web cookies
              ));

              FrontendLogger.debug('Token refresh successful.');
              this.isRefreshing = false;
              this.refreshTokenSubject.next(true); // Signal that refresh is complete

              // Handle new tokens for dontUserCookie mode
              if (this.dontUserCookie && refreshResponse?.data) {
                const newAccessToken = refreshResponse.data.access_token; // Assuming response format
                const newRefreshToken = refreshResponse.data.refresh_token; // Assuming response format

                if (newAccessToken && newRefreshToken) {
                  await this.storeNewTokensAfterRefresh(newAccessToken, newRefreshToken);
                  this.appendToken(newAccessToken); // Update internal state for future requests
                  FrontendLogger.debug('dontUserCookie mode: New tokens stored and internal state updated.');
                } else {
                  FrontendLogger
                    .warn(`dontUserCookie mode: Refresh response missing 
                      new access/refresh tokens. Cannot update local state.`);
                  this.onRefreshFailure(); // Treat as refresh failure if tokens aren't returned

                  return await Promise.reject(new BizNestClientError('Invalid token refresh response.'));
                }
              } else if (!this.dontUserCookie) {
                FrontendLogger.debug('Web: New cookies implicitly handled by browser after refresh.');
              }

              // Retry the original request
              FrontendLogger.debug('Retrying original request...');

              // 🔑 EXPLICITLY SET THE NEW TOKEN ON THE ORIGINAL REQUEST CONFIG
              const config: AxiosRequestConfig = {
                ...originalRequest,
                headers: originalRequest.headers || {} as AxiosRequestHeaders
              };

              if (!config.headers) {
                config.headers = {};
              }

              // Use the token that was just set to the internal state: this.currentAccessToken
              config.headers['Authorization'] = this.currentAccessToken;

              return await firstValueFrom(Axios.request(config));
            } catch (refreshError) {
              this.isRefreshing = false;
              this.refreshTokenSubject.error(refreshError); // Propagate refresh error
              FrontendLogger.error(refreshError, 'Token refresh failed. Executing onRefreshFailure callback.');

              // On refresh failure (e.g., refresh token expired or invalid), execute the provided callback
              this.onRefreshFailure();

              return await Promise.reject(refreshError); // Reject the original request's observable
            } finally {
              this.isRefreshing = false; // Ensure flag is reset
            }
          } else {
            // Token refresh is already in progress, queue this request
            FrontendLogger.debug('Token refresh already in progress. Queueing request...');

            return firstValueFrom(this.refreshTokenSubject.pipe(
              // eslint-disable-next-line max-len
              filter(val => val !== null), // Wait until refreshTokenSubject emits a non-null value (i.e., new token available)
              take(1), // Take only one value then complete
              switchMap(() => {
                FrontendLogger.debug('Queued request retrying after refresh...');

                return Axios.request(originalRequest); // Retry the original request
              })
            ));
          }
        }

        // For any other error (not 401 or not eligible for retry), just reject
        return Promise.reject(error);
      }
    );
  }


  /**
   * Appends custom headers to all subsequent requests.
   * @param headers - Key-value pairs of headers to add
   * @throws {BizNestClientError} If headers are invalid
   */
  appendToHeaders(headers: { [key: string]: string }) {
    if (!headers) {
      throw new BizNestClientError('Headers are required');
    }

    if (typeof headers !== 'object') {
      throw new BizNestClientError('Headers must be an object');
    }

    FrontendLogger.debug('appendToHeaders: Adding headers', headers);
    this.headers = { ...this.headers, ...headers };
  }

  /**
   * Replaces all existing headers with the provided ones.
   * @param headers - New headers object
   * @throws {BizNestClientError} If headers are invalid
   */
  setHeaders(headers: { [key: string]: string }) {
    if (!headers) {
      throw new BizNestClientError('Headers are required');
    }

    if (typeof headers !== 'object') {
      throw new BizNestClientError('Headers must be an object');
    }

    FrontendLogger.debug('setHeaders: Setting new headers', headers);
    this.headers = { ...headers };
  }

  /**
   * Removes all custom headers from subsequent requests.
   */
  clearHeaders() {
    this.headers = {};
    FrontendLogger.debug('clearHeaders: All custom headers cleared');
  }

  /**
   * Performs a GET request.
   * @template T - Expected response type
   * @param route - API endpoint path
   * @param retryTimes - Number of retry attempts (default: 5)
   * @returns Observable that emits the response data
   * @throws {BizNestClientError} If route is invalid
   */
  makeGet<T = void>(route: string, retryTimes = 5): Observable<T> {
    if (!route) {
      throw new BizNestClientError('Route is required');
    }

    FrontendLogger.debug('makeGet: Sending request', { route });

    return this.axiosInstance.get(route).pipe(
      retry(retryTimes),
      map(res => {
        FrontendLogger.debug('makeGet: Received response', { route, data: res.data });

        return res.data;
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Performs a PUT request.
   * @template T - Expected response type
   * @param route - API endpoint path
   * @param data - Request payload
   * @returns Observable that emits the response data
   * @throws {BizNestClientError} If route is invalid
   */
  makePut<T = void>(route: string, data: unknown): Observable<T> {
    if (!route) {
      throw new BizNestClientError('Route is required');
    }

    FrontendLogger.debug('makePut: Sending request', { route, data });

    return this.axiosInstance.put(route, data).pipe(
      map(res => {
        FrontendLogger.debug('makePut: Received response', { route, data: res.data });

        return res.data;
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Performs a POST request.
   * @template T - Expected response type
   * @param route - API endpoint path
   * @param data - Request payload
   * @returns Observable that emits the response data
   * @throws {BizNestClientError} If route is invalid
   */
  makePost<T = void>(route: string, data: unknown): Observable<T> {
    if (!route) {
      throw new BizNestClientError('Route is required');
    }

    FrontendLogger.debug('makePost: Sending request', { route, data });

    return this.axiosInstance.post(route, data).pipe(
      map(res => {
        FrontendLogger.debug('makePost: Received response', { route, data: res.data });

        return res.data;
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Performs a DELETE request.
   * @template T - Expected response type
   * @param route - API endpoint path
   * @returns Observable that emits the response data
   * @throws {BizNestClientError} If route is invalid
   */
  makeDelete<T = void>(route: string): Observable<T> {
    if (!route) {
      throw new BizNestClientError('Route is required');
    }

    FrontendLogger.debug('makeDelete: Sending request', { route });

    return this.axiosInstance.delete(route).pipe(
      map(res => {
        FrontendLogger.debug('makeDelete: Received response', { route, data: res.data });

        return res.data;
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * A method that uploads files to the specified URL.
   * @param files - An array of files to upload.

   * @param url - The URL to upload the files to.
   * @param extras - Any extra data to include in the request.
   * @returns An Observable that emits the response data.
   */
  uploadFiles<T = Isuccess>(
    files: Ifile[],
    url: string,
    extras?
  ):
    Observable<IUploadResponse<T>> {
    if (!url) {
      throw new BizNestClientError('Route is required');
    }

    FrontendLogger.debug('uploadFiles: Sending request', { url, files, extras });

    // Create a new FormData object.
    const formData: FormData = new FormData();

    // If the `extras` parameter is not null, then append it to the form data.
    if (extras) {
      formData.append('data', JSON.stringify(extras));
    }

    // For each file in the `files` array, append it to the form data.
    for (const file of files) {
      formData.append(file.fieldname, file.blob);
    }

    // Log the request.
    FrontendLogger.debug('uploadFiles:: - url: %url , formData: %formData ', url, formData);

    // Create a Subject to emit progress updates
    const progressSubject = new Subject<IUploadProgress>();

    // Create Axios config with progress tracking
    const config = {
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        const progress: IUploadProgress = {
          percent: percentCompleted,
          loaded: progressEvent.loaded,
          total: progressEvent.total
        };

        progressSubject.next(progress);
        FrontendLogger.debug('Upload progress: %d%', percentCompleted);
      }
    };

    // Create the Observable for the response
    const response$ = this.axiosInstance.post(url, formData, config).pipe(
      map(res => {
        FrontendLogger.debug('uploadFiles', res.data);

        return {
          progress: progressSubject.asObservable(),
          response: of(res.data)
        };
      }),
      catchError(error => throwError(() => error))
    );

    return response$;
  }


  /**
   * Determines if a refresh token should be added to the request.
   * @private
   */
  private shouldAddRefreshToken(config: AxiosRequestConfig): boolean {
    const url = config.url || '';

    return (
      (this.dontUserCookie && (url.includes('/auth/logout') || url.includes('/auth/refresh'))) ||
      url.includes('/auth/ws-login') ||
      url.includes('/auth/ws-refresh')
    );
  }

  /**
   * Checks if refresh endpoint retry limit has been exceeded
   * @private
   */
  private isRefreshRetryLimitExceeded(url: string): boolean {
    const normalizedUrl = url.replace(this.authServiceBaseUrl, '');

    if (!['/auth/refresh', '/auth/ws-refresh'].includes(normalizedUrl)) {
      return false;
    }

    const now = Date.now();
    const retryState = this.refreshRetryState[normalizedUrl];

    // Reset count if outside time window
    if (now - retryState.timestamp > this.REFRESH_RETRY_WINDOW_MS) {
      retryState.count = 0;
      retryState.timestamp = now;

      return false;
    }

    // Check if limit exceeded
    if (retryState.count >= this.REFRESH_RETRY_LIMIT) {
      FrontendLogger.error(`Refresh retry limit exceeded for ${normalizedUrl}. Resetting state.`);
      retryState.count = 0;
      retryState.timestamp = now;

      return true;
    }

    // Increment retry count
    retryState.count++;
    retryState.timestamp = now;

    return false;
  }
}
